import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import {
  FRONT_URL,
  addMembersToSelfGroup,
  createSelfGroup,
  deleteSelfGroup,
  getAvailableUsersForSelfGroup,
  getSelfGroupsByCategory,
  removeMemberFromSelfGroup,
  sendSelfGroupLaunchEmails,
  sendSelfGroupReminderEmails,
  updateSelfGroup,
} from "../axios/axiosFunctions";
import DeleteConfirmationModal from "./common/DeleteConfirmationModal";

const SelfGroups = ({ category, assessmentId }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMsg, setAlertMsg] = useState("");

  // Create group
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // Edit group
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState({ id: "", name: "" });

  // Delete group
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: "", name: "" });

  // Add members
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [addMembersGroup, setAddMembersGroup] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogType, setEmailDialogType] = useState("launch");
  const [emailTargetGroup, setEmailTargetGroup] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const showAlert = (severity, msg, ms = 2500) => {
    setAlertSeverity(severity);
    setAlertMsg(msg);
    setAlertOpen(true);
    setTimeout(() => setAlertOpen(false), ms);
  };

  const fetchGroups = async () => {
    setLoading(true);
    const res = await getSelfGroupsByCategory(category);
    if (res && !res.error) {
      setGroups(res.groups || []);
    } else {
      setGroups([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (category) fetchGroups();
  }, [category]);

  // ---------- Create ----------
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showAlert("error", "Group name is required");
      return;
    }
    const res = await createSelfGroup({ name: newGroupName.trim(), category });
    if (res?.error) {
      showAlert("error", res.msg || "Error creating group");
      return;
    }
    setNewGroupName("");
    setCreateOpen(false);
    showAlert("success", "Group created");
    fetchGroups();
  };

  // ---------- Edit ----------
  const handleOpenEdit = (e, group) => {
    e.stopPropagation();
    setEditTarget({ id: group._id, name: group.name });
    setEditOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editTarget.name.trim()) {
      showAlert("error", "Group name is required");
      return;
    }
    const res = await updateSelfGroup(editTarget.id, { name: editTarget.name.trim() });
    if (res?.error) {
      showAlert("error", res.msg || "Error updating group");
      return;
    }
    setEditOpen(false);
    showAlert("success", "Group updated");
    fetchGroups();
  };

  // ---------- Delete ----------
  const handleOpenDelete = (e, group) => {
    e.stopPropagation();
    setDeleteTarget({ id: group._id, name: group.name });
    setDeleteOpen(true);
  };

  const handleDeleteGroup = async () => {
    const res = await deleteSelfGroup(deleteTarget.id);
    setDeleteOpen(false);
    if (res?.error) {
      showAlert("error", res.msg || "Error deleting group");
      return;
    }
    showAlert("success", "Group deleted");
    fetchGroups();
  };

  // ---------- Add members ----------
  const mapAutocompleteOptions = (usersArray) => {
    if (!usersArray || usersArray.length === 0) {
      setAutocompleteOptions([]);
      return;
    }
    setAutocompleteOptions(
      usersArray.map((u, i) => ({
        label: `${u.firstName || ""}${u.lastName ? ` ${u.lastName}` : ""} — ${u.email}`,
        index: i,
      }))
    );
  };

  const handleOpenAddMembers = async (e, group) => {
    e.stopPropagation();
    setAddMembersGroup(group);
    setPendingUsers([]);
    setAddMembersOpen(true);
    const res = await getAvailableUsersForSelfGroup(group._id, assessmentId);
    if (res?.error) {
      showAlert("error", res.msg || "Failed to load available users");
      setAvailableUsers([]);
      mapAutocompleteOptions([]);
      return;
    }
    setAvailableUsers(res.users || []);
    mapAutocompleteOptions(res.users || []);
  };

  const handleCloseAddMembers = () => {
    setAddMembersOpen(false);
    setAddMembersGroup(null);
    setPendingUsers([]);
    setAvailableUsers([]);
    setAutocompleteOptions([]);
  };

  const handleAutocompleteSelect = (e, newValue) => {
    if (!newValue) return;
    const { index } = newValue;
    const selected = availableUsers[index];
    if (!selected) return;
    const nextAvailable = [
      ...availableUsers.slice(0, index),
      ...availableUsers.slice(index + 1),
    ];
    setAvailableUsers(nextAvailable);
    mapAutocompleteOptions(nextAvailable);
    setPendingUsers((prev) => [...prev, selected]);
  };

  const handleConfirmAddMembers = async () => {
    if (!addMembersGroup || pendingUsers.length === 0) {
      handleCloseAddMembers();
      return;
    }
    const userIds = pendingUsers.map((u) => u._id);
    const res = await addMembersToSelfGroup(addMembersGroup._id, userIds);
    if (res?.error) {
      showAlert("error", res.msg || "Failed to add members");
      return;
    }
    showAlert("success", `${pendingUsers.length} member(s) added`);
    handleCloseAddMembers();
    fetchGroups();
  };

  // ---------- Remove member ----------
  const handleRemoveMember = async (groupId, userId) => {
    const res = await removeMemberFromSelfGroup(groupId, userId);
    if (res?.error) {
      showAlert("error", res.msg || "Failed to remove member");
      return;
    }
    showAlert("success", "Member removed");
    fetchGroups();
  };

  // ---------- Send launch / reminder ----------
  const handleOpenEmailDialog = (e, group, type) => {
    e.stopPropagation();
    setEmailTargetGroup(group);
    setEmailDialogType(type);
    setEmailResult(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    if (sendingEmails) return;
    setEmailDialogOpen(false);
    setEmailTargetGroup(null);
    setEmailResult(null);
  };

  const handleSendGroupEmails = async () => {
    if (!emailTargetGroup) return;
    setSendingEmails(true);
    setEmailResult(null);
    try {
      const fn =
        emailDialogType === "launch"
          ? sendSelfGroupLaunchEmails
          : sendSelfGroupReminderEmails;
      const data = { frontUrl: FRONT_URL };
      if (emailDialogType === "reminder") data.assessmentId = assessmentId;

      const res = await fn(emailTargetGroup._id, data);
      if (res?.error) {
        setEmailResult({ severity: "error", msg: res.msg || "Failed to send emails." });
      } else {
        const sent = res?.sent ?? 0;
        const failed = res?.failed ?? 0;
        const skipped = res?.skippedCompleted ?? 0;
        let msg = `${emailDialogType === "launch" ? "Launch" : "Reminder"} emails sent: ${sent}.`;
        if (skipped > 0) msg += ` Skipped ${skipped} (already completed).`;
        if (failed > 0) msg += ` Failed: ${failed}.`;
        setEmailResult({
          severity: failed > 0 ? "warning" : "success",
          msg,
        });
        fetchGroups();
      }
    } catch (err) {
      console.error(err);
      setEmailResult({ severity: "error", msg: "Failed to send emails." });
    } finally {
      setSendingEmails(false);
    }
  };

  const emailTargetLabel = emailTargetGroup
    ? `${emailTargetGroup.name} (${emailTargetGroup.members?.length || 0} member${
        (emailTargetGroup.members?.length || 0) === 1 ? "" : "s"
      })`
    : "";

  return (
    <Box sx={{ marginTop: "40px", marginBottom: "60px" }}>
      <Typography variant="h4" fontWeight={500} color="secondary">
        Groups
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Organize users into groups to send launch emails or reminders to a
        subset instead of every user.
      </Typography>
      <Button
        onClick={() => setCreateOpen(true)}
        variant="contained"
        sx={{ color: "white", marginTop: "20px", marginBottom: "20px" }}
        endIcon={<AddCircleOutlineIcon />}
      >
        ADD GROUP
      </Button>
      {alertOpen && (
        <Alert sx={{ marginBottom: "20px" }} severity={alertSeverity}>
          {alertMsg}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Typography variant="h6" color="text.secondary">
          No groups yet
        </Typography>
      ) : (
        groups.map((g) => (
          <Accordion key={g._id} sx={{ marginBottom: "20px" }}>
            <AccordionSummary>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <GroupIcon color="secondary" />
                  <Typography variant="h5">{g.name}</Typography>
                  <Chip
                    size="small"
                    label={`${g.members?.length || 0} member${
                      (g.members?.length || 0) === 1 ? "" : "s"
                    }`}
                  />
                </Box>
                <Box>
                  <Tooltip title="Add members">
                    <IconButton
                      onClick={(e) => handleOpenAddMembers(e, g)}
                      color="primary"
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send launch email to group">
                    <span>
                      <IconButton
                        onClick={(e) => handleOpenEmailDialog(e, g, "launch")}
                        size="small"
                        disabled={(g.members?.length || 0) === 0}
                        sx={{ color: (g.members?.length || 0) === 0 ? undefined : "#ed6c02", mr: 0.5 }}
                      >
                        <EmailIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Send reminder email to group">
                    <span>
                      <IconButton
                        onClick={(e) => handleOpenEmailDialog(e, g, "reminder")}
                        size="small"
                        disabled={(g.members?.length || 0) === 0}
                        sx={{ color: (g.members?.length || 0) === 0 ? undefined : "#0288d1", mr: 0.5 }}
                      >
                        <NotificationsActiveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rename group">
                    <IconButton
                      onClick={(e) => handleOpenEdit(e, g)}
                      color="primary"
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete group">
                    <IconButton
                      onClick={(e) => handleOpenDelete(e, g)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {g.members && g.members.length > 0 ? (
                <List dense>
                  {g.members.map((m) => (
                    <ListItem key={m._id}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${m.firstName || ""}${m.lastName ? ` ${m.lastName}` : ""}`}
                        secondary={m.email}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove from group">
                          <IconButton
                            edge="end"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveMember(g._id, m._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No members yet — use the “Add members” button above.
                </Typography>
              )}
              {(g.launchDate || g.lastReminderSent) && (
                <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {g.launchDate && (
                    <Typography variant="caption" color="text.secondary">
                      Last launch: {new Date(g.launchDate).toLocaleString()}
                    </Typography>
                  )}
                  {g.lastReminderSent && (
                    <Typography variant="caption" color="text.secondary">
                      Last reminder: {new Date(g.lastReminderSent).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Create group dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Group Name"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateGroup}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit group dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Group Name"
            fullWidth
            value={editTarget.name}
            onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdateGroup}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add members dialog */}
      <Dialog open={addMembersOpen} onClose={handleCloseAddMembers} maxWidth="md" fullWidth>
        <DialogTitle>
          Add Members
          {addMembersGroup ? ` — ${addMembersGroup.name}` : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Only users already on this assessment can be added.
          </Typography>
          <Autocomplete
            options={autocompleteOptions}
            onChange={handleAutocompleteSelect}
            value={null}
            renderInput={(params) => <TextField {...params} label="Add user" />}
          />
          <Divider sx={{ my: 2 }} />
          {pendingUsers.length > 0 ? (
            <>
              <Typography variant="subtitle1" fontWeight={500} color="secondary">
                Users to Add
              </Typography>
              <List>
                {pendingUsers.map((u) => (
                  <ListItem key={u._id}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${u.firstName || ""}${u.lastName ? ` ${u.lastName}` : ""}`}
                      secondary={u.email}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No users selected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddMembers} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAddMembers}
            disabled={pendingUsers.length === 0}
          >
            Add {pendingUsers.length > 0 ? `(${pendingUsers.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send launch / reminder dialog */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog}>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {emailDialogType === "launch" ? "Send Launch Emails" : "Send Reminder Emails"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {emailDialogType === "launch" ? (
              <>
                Send launch email to <strong>{emailTargetLabel}</strong>.
              </>
            ) : (
              <>
                Send reminder to <strong>{emailTargetLabel}</strong> — users who have
                already completed the assessment will be skipped.
              </>
            )}
          </Typography>
          {emailResult && (
            <Alert severity={emailResult.severity} sx={{ mt: 1 }}>
              {emailResult.msg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button onClick={handleCloseEmailDialog} disabled={sendingEmails} color="inherit">
            Cancel
          </Button>
          {!emailResult ? (
            <Button
              onClick={handleSendGroupEmails}
              variant="contained"
              color={emailDialogType === "launch" ? "primary" : "info"}
              disabled={sendingEmails}
              startIcon={
                sendingEmails ? (
                  <CircularProgress size={18} color="inherit" />
                ) : emailDialogType === "launch" ? (
                  <EmailIcon />
                ) : (
                  <NotificationsActiveIcon />
                )
              }
            >
              {sendingEmails ? "Sending..." : "Send"}
            </Button>
          ) : (
            <Button onClick={handleCloseEmailDialog} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete group confirmation */}
      <DeleteConfirmationModal
        openDialog={deleteOpen}
        closeDialog={() => setDeleteOpen(false)}
        title={`Delete Group: ${deleteTarget.name}`}
        message="Members of this group will not be deleted from the assessment — only the group itself will be removed."
        onConfirm={handleDeleteGroup}
      />
    </Box>
  );
};

export default SelfGroups;
